import React, { useMemo } from "react";
import CustomTable from "components/Table/CustomTable";
import { Button } from "reactstrap";
import { Link } from "react-router-dom";
import { EarningsSchema } from "model/transactions/earnings.schema";
import { formatCurrency } from "utils/formatters";
import useTableChangeHandler from "hooks/useTableChangeHandler";
import Avatar from "components/Avatar/Avatar";

const EarningsTable = ({ data, onTableChange, error, isLoading }) => {
  const { handleSort, handleFilter, handleSearch, handlePageChange } = useTableChangeHandler(onTableChange);

  const getStatusBadge = (status) => {
    switch (status) {
      case "Paid":
        return <div className="text-success-600 fw-semibold">จ่ายเงินแล้ว</div>;
      case "Pending":
        return <div className="text-gray-400 fw-semibold">รอดำเนินการ</div>;
      case "Cancelled":
        return <div className="text-danger-400 fw-semibold">ไม่สำเร็จ</div>;
      default:
        return <div className="text-gray-400 fw-semibold">-</div>;
    }
  };

  const columns = useMemo(
    () => [
      {
        header: "ลำดับ",
        accessor: "number",
        width: 40,
        align: "center",
        render: (_, index, page, pageSize) => <div className="fw-semibold">{(page - 1) * pageSize + index + 1}</div>,
      },
      {
        header: "Affiliate พาร์ทเนอร์",
        accessor: "name",
        sortable: true,
        render: (item) => (
          <div className="partner-info d-flex align-items-center gap-3">
            <Avatar src={item.partner.profilePicture} alt={`${item.partner.firstName} ${item.partner.lastName}`} />
            <div>
              <div className="text-lg fw-semibold">{`${item.partner.firstName} ${item.partner.lastName}`}</div>
              <div className="text-md text-gray-500">{item.partner.email}</div>
            </div>
          </div>
        ),
      },
      {
        header: "ไอดีพาร์ทเนอร์",
        accessor: "partner.id",
        responsive: "lg",
      },
      {
        header: "ประเภท Affiliator",
        accessor: "partner.partnerType",
        responsive: "lg",
        render: (item) => <div>{item.partner.partnerType == "Individual" ? "บุคคลทั่วไป" : "บริษัท"}</div>,
      },
      {
        header: "ยอดทั้งหมด",
        accessor: "totalSales",
        sortable: true,
        render: (item) => <div className="fw-semibold">{formatCurrency(item.totalSales)}</div>,
      },
      {
        header: "รายได้ทั้งหมด",
        accessor: "totalEarnings",
        sortable: true,
        render: (item) => <div className="fw-semibold">{formatCurrency(item.totalEarnings)}</div>,
      },
      {
        header: "สถานะ",
        accessor: "status",
        sortable: true,
        render: (item) => getStatusBadge(item.status),
      },
      {
        header: "",
        header: "รายละเอียด",
        accessor: "actions",
        width: 50,
        render: (item) => (
          <Link to={`/transactions/${item.id}`} className="light d-flex justify-content-start gap-2 btn btn-primary btn-sm text-white">
            ดูรายละเอียด
          </Link>
        ),
      },
    ],
    []
  );

  const handleViewDetails = async (item) => {};

  return (
    <div>
      <div className="text-lg mb-4">รายการธุรกรรมทั้งหมด * {data?.pagination?.total?.toLocaleString() || 0} รายการ *</div>

      <CustomTable columns={columns} data={data?.data || []} itemsPerPage={data?.pagination?.limit || 10} totalItems={data?.pagination?.total || 0} onSort={handleSort} onFilter={handleFilter} onSearch={handleSearch} onPageChange={handlePageChange} status={isLoading ? "loading" : error ? "error" : data?.data?.length === 0 ? "empty" : "normal"} errorMessage={error?.message || "เกิดข้อผิดพลาดในการโหลดข้อมูล"} emptyMessage="ไม่พบข้อมูลพาร์ทเนอร์ที่ทำธุรกรรม" />
    </div>
  );
};

const withYupValidation = (WrappedComponent, schema) => {
  return function WithYupValidation(props) {
    try {
      if (!props.isLoading && !props.error && props.data) {
        schema.validateSync(props.data);
      }
      return <WrappedComponent {...props} />;
    } catch (error) {
      return (
        <div className="alert alert-danger">
          <strong>Validation Error:</strong> {error.message}
        </div>
      );
    }
  };
};

const ValidatedTable = withYupValidation(EarningsTable, EarningsSchema);

export default ValidatedTable;
